import { useState, useRef } from "react";
import { motion, useInView, AnimatePresence } from "framer-motion";
import { Plus, Minus } from "lucide-react";
import CTABand from "../components/shared/CTABand";

function RevealSection({ children, delay = 0, className = "" }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-60px" });
  return (
    <motion.div ref={ref} initial={{ opacity: 0, y: 32 }} animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.7, delay, ease: [0.16, 1, 0.3, 1] }} className={className}>
      {children}
    </motion.div>
  );
}

function AccordionItem({ question, answer, isOpen, onToggle, index }) {
  return (
    <div className="accordion-item" data-testid={`faq-item-${index}`}>
      <button
        onClick={onToggle}
        className="accordion-trigger"
        aria-expanded={isOpen}
        data-testid={`faq-trigger-${index}`}
      >
        <span className="pr-4 text-left">{question}</span>
        <div className="w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0"
          style={{ background: isOpen ? "rgba(0,212,255,0.12)" : "var(--surface-2)", border: `1px solid ${isOpen ? "rgba(0,212,255,0.3)" : "var(--border)"}` }}>
          {isOpen ? <Minus size={13} style={{ color: "var(--cyan)" }} /> : <Plus size={13} style={{ color: "var(--text-muted)" }} />}
        </div>
      </button>
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3, ease: "easeInOut" }}
            style={{ overflow: "hidden" }}
            data-testid={`faq-answer-${index}`}
          >
            <div className="pb-6 text-base leading-relaxed" style={{ color: "var(--text-muted)" }}>
              {answer}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

const FAQ_SECTIONS = [
  {
    title: "The Event",
    items: [
      { q: "When and where is Core AI Summit 2027?", a: "Core AI Summit 2027 is scheduled for June 2027 in Niagara Falls, Ontario. The exact dates and venue are being finalized and will be announced soon. Join the priority list to be notified immediately when details are confirmed." },
      { q: "How many people attend Core AI Summit?", a: "We're targeting 1,200–1,500 attendees for the inaugural event — a deliberate size that enables meaningful connections without the chaos of a mega-conference. Quality over quantity is a core principle." },
      { q: "How is Core AI Summit different from other AI events?", a: "Core AI Summit is built on three differentiators: practitioner-first content (real implementations, not theory), AI-powered networking (curated 1:1 meetings, not chance encounters), and a focus on measurable outcomes (not just inspiration). It's Canada's event — built for the Canadian and global AI ecosystem." },
      { q: "Will there be a virtual or hybrid option?", a: "We're focused on making the in-person experience exceptional for the inaugural event. Session recordings and virtual networking options may be announced closer to the event. Join the priority list for updates." },
    ],
  },
  {
    title: "Tickets & Registration",
    items: [
      { q: "How do I buy tickets?", a: "Tickets are not yet on sale. Join the priority list to be the first to know when registration opens and to access early-bird pricing before it's available to the general public." },
      { q: "What are the ticket types and pricing?", a: "There are three ticket tiers: Business & SMB ($399–$599 CAD), Enterprise & Leadership ($699–$1,099 CAD), and Technical & Builders ($449–$749 CAD). All tiers include full access to all sessions. A VIP upgrade is also available. Prices are indicative — early-bird pricing is available to priority list members." },
      { q: "Are there student or academic discounts?", a: "Yes. A 50% student discount is available for the Technical & Builders ticket tier. Academic and non-profit organization discounts may also be available. Details will be confirmed with registration launch." },
      { q: "Are group discounts available?", a: "Group discounts are available for Enterprise ticket purchases of 3 or more. Contact us directly at info@coreaisummit.com for group booking inquiries." },
    ],
  },
  {
    title: "Speakers & Content",
    items: [
      { q: "How do I apply to speak?", a: "Speaker applications are open on the Speakers page. We prioritize practitioners — people who have actually implemented AI and can share real outcomes. All applications are reviewed by our program team. Notification timelines will be communicated with the speaker program launch." },
      { q: "What kind of content does Core AI Summit prioritize?", a: "We explicitly prioritize practitioner case studies over vendor pitches. The ideal Core AI speaker has implemented AI in a real organization and can share what worked, what didn't, and what they'd do differently. We want fewer, stronger sessions — not a packed schedule of surface-level talks." },
      { q: "When will speakers be announced?", a: "Speaker announcements will begin on a rolling basis. Priority list members will be notified with each major announcement batch. Follow us on LinkedIn and join the priority list for the fastest updates." },
    ],
  },
  {
    title: "Sponsors & Exhibitors",
    items: [
      { q: "How do I become a sponsor or exhibitor?", a: "Visit the Partner page to view sponsorship tiers and exhibitor packages, or fill out the inquiry form to request the full prospectus. Our partnerships team will be in touch within 2 business days." },
      { q: "What sponsorship tiers are available?", a: "We offer four main tiers: Title Sponsor ($100K CAD, 1 exclusive slot), Platinum ($60K CAD, 2 available), Gold ($40K CAD, 5 available), and Silver ($10K CAD, 10 available). Exhibitor booth options include Premium, Standard, and Startup Pods." },
      { q: "Is there a Startup Zone?", a: "Yes. The Core AI Startup & Demo Zone is an application-based showcase for emerging AI companies. It includes dedicated pitch sessions, direct access to enterprise buyers, and investor networking access. Startup Pods are $2,500 CAD." },
    ],
  },
  {
    title: "Networking & App",
    items: [
      { q: "How does the AI networking work?", a: "Core AI Summit uses an AI-powered matching system to curate 1:1 meeting recommendations based on your profile, goals, and pathway. You can book up to 10 pre-scheduled meetings through the event app before you arrive. The system also powers roundtable placements and session recommendations." },
      { q: "What is COREy?", a: "COREy is the AI concierge personality of Core AI Summit. You can interact with COREy in plain language to get curated session recommendations, networking suggestions, and exhibitor matches. COREy will be available through the event app, launching in 2027." },
      { q: "Will there be an event app?", a: "Yes. The Core AI EventOS app will include your personal agenda, 1:1 meeting booking, COREy AI concierge, exhibitor directory, and networking features. It will be available for download before the event." },
    ],
  },
];

export default function FAQPage() {
  const [openItems, setOpenItems] = useState({});
  const toggle = (sectionIdx, itemIdx) => {
    const key = `${sectionIdx}-${itemIdx}`;
    setOpenItems(prev => ({ ...prev, [key]: !prev[key] }));
  };

  return (
    <div data-testid="faq-page">
      <section className="page-hero" style={{ backgroundColor: "var(--surface)" }} data-testid="faq-hero">
        <div className="container-xl">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}>
            <div className="overline-label mb-4">Questions & Answers</div>
            <h1 className="text-5xl sm:text-6xl font-black tracking-tighter text-white mb-6"
              style={{ fontFamily: "var(--font-heading)" }}>FAQ.</h1>
            <p className="text-lg max-w-2xl" style={{ color: "var(--text-muted)" }}>
              Everything you need to know about Core AI Summit 2027. Can't find your answer? Contact us at info@coreaisummit.com
            </p>
          </motion.div>
        </div>
      </section>

      <section className="section-py" data-testid="faq-content-section">
        <div className="container-xl">
          <div className="max-w-3xl mx-auto">
            {FAQ_SECTIONS.map((section, sIdx) => (
              <RevealSection key={section.title} delay={sIdx * 0.05}>
                <div className="mb-12" data-testid={`faq-section-${sIdx}`}>
                  <div className="overline-label mb-6">{section.title}</div>
                  <div>
                    {section.items.map((item, iIdx) => (
                      <AccordionItem
                        key={iIdx}
                        question={item.q}
                        answer={item.a}
                        isOpen={!!openItems[`${sIdx}-${iIdx}`]}
                        onToggle={() => toggle(sIdx, iIdx)}
                        index={`${sIdx}-${iIdx}`}
                      />
                    ))}
                  </div>
                </div>
              </RevealSection>
            ))}

            <RevealSection delay={0.3}>
              <div className="mt-8 p-6 rounded-2xl text-center"
                style={{ background: "var(--surface)", border: "1px solid var(--border)" }}>
                <div className="overline-label mb-3">Still Have Questions?</div>
                <p className="text-base mb-4" style={{ color: "var(--text-muted)" }}>
                  Our team is happy to help. Reach out directly and we'll get back to you quickly.
                </p>
                <a href="mailto:info@coreaisummit.com" className="btn-outline" data-testid="faq-contact-link">
                  info@coreaisummit.com
                </a>
              </div>
            </RevealSection>
          </div>
        </div>
      </section>

      <CTABand
        title="Ready to Join Core AI Summit?"
        subtitle="Questions answered. Now let's get you on the priority list."
        primaryLabel="Join Priority List"
      />
    </div>
  );
}
